import type { MetaFunction } from "@remix-run/cloudflare";
import { Link } from "@remix-run/react";
import { 
  BookOpen, 
  Download, 
  AlertTriangle, 
  Zap, 
  Settings,
  ArrowRight,
  Clock,
  Eye,
  Star
} from "lucide-react";

export const meta: MetaFunction = () => {
  return [
    { title: "Knowledge Base - Stellar Blade Mods Wiki" },
    { 
      name: "description", 
      content: "Comprehensive knowledge base for Stellar Blade modding. Find detailed guides on installation, troubleshooting, advanced techniques, and modding tools." 
    },
    { name: "keywords", content: "stellar blade,knowledge base,mod guide,installation guide,troubleshooting,advanced modding,tools" },
    { name: "robots", content: "index,follow" },
    { rel: "canonical", href: "https://stellarblademod.com/wiki/knowledge-base" },
    
    // Open Graph
    { property: "og:title", content: "Knowledge Base - Stellar Blade Mods Wiki" },
    { property: "og:description", content: "Comprehensive knowledge base for Stellar Blade modding. Find detailed guides on installation, troubleshooting, advanced techniques, and modding tools." },
    { property: "og:url", content: "https://stellarblademod.com/wiki/knowledge-base" },
    { property: "og:type", content: "website" },
    
    // Twitter Card
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: "Knowledge Base - Stellar Blade Mods Wiki" },
    { name: "twitter:description", content: "Comprehensive knowledge base for Stellar Blade modding. Find detailed guides on installation, troubleshooting, advanced techniques, and modding tools." },
  ];
};

const categories = [
  {
    id: "installation",
    title: "Installation Guides",
    description: "Step-by-step guides for installing mods safely and correctly",
    icon: Download,
    color: "from-green-400 to-emerald-500",
    borderColor: "border-green-400/50",
    shadowColor: "shadow-green-400/25",
    articles: [
      {
        title: "Basic Mod Installation Guide",
        description: "Learn the fundamentals of installing Stellar Blade mods",
        readTime: "8 min",
        views: "15.2k",
        rating: 4.8
      },
      {
        title: "Installing Multiple Mods",
        description: "How to properly install and manage multiple mods",
        readTime: "12 min", 
        views: "9.7k",
        rating: 4.6
      },
      {
        title: "Manual vs Automatic Installation",
        description: "Understanding different installation methods",
        readTime: "6 min",
        views: "7.3k", 
        rating: 4.5
      }
    ]
  },
  {
    id: "troubleshooting",
    title: "Troubleshooting",
    description: "Solutions for common mod issues and conflicts",
    icon: AlertTriangle,
    color: "from-yellow-400 to-orange-500",
    borderColor: "border-yellow-400/50",
    shadowColor: "shadow-yellow-400/25",
    articles: [
        {
            title: "Suspicious Mod Files and Security Checks",
            description: "Identify and verify potentially harmful mod files",
            readTime: "8 min",
            views: "3.1k",
            rating: 4.8
          },
      {
        title: "Fixing Mod Conflicts and Load Order",
        description: "Resolve conflicts between multiple mods",
        readTime: "12 min",
        views: "12.8k",
        rating: 4.9
      },
      {
        title: "Game Crashes After Installing Mods",
        description: "Diagnose and fix crash issues",
        readTime: "10 min",
        views: "8.4k",
        rating: 4.4
      }
    ]
  },
  {
    id: "advanced",
    title: "Advanced Techniques",
    description: "Expert-level modding techniques and customizations",
    icon: Zap,
    color: "from-cyan-400 to-blue-500",
    borderColor: "border-cyan-400/50", 
    shadowColor: "shadow-cyan-400/25",
    articles: [
      {
        title: "Creating Custom Mod Configurations",
        description: "Advanced configuration and customization options",
        readTime: "20 min",
        views: "5.2k",
        rating: 4.6
      },
      {
        title: "Mod Development Basics",
        description: "Getting started with creating your own mods",
        readTime: "25 min",
        views: "3.8k",
        rating: 4.8
      },
      {
        title: "Texture Replacement Techniques",
        description: "Advanced texture modding and replacement",
        readTime: "18 min",
        views: "4.1k",
        rating: 4.5
      }
    ]
  },
  {
    id: "tools",
    title: "Tools & Resources",
    description: "Essential tools and resources for modding",
    icon: Settings,
    color: "from-indigo-400 to-purple-500",
    borderColor: "border-indigo-400/50",
    shadowColor: "shadow-indigo-400/25",
    articles: [
      {
        title: "Essential Modding Tools Setup",
        description: "Must-have tools for every modder",
        readTime: "6 min",
        views: "9.4k",
        rating: 4.7
      },
      {
        title: "Backup and Save Management",
        description: "Protecting your saves and game files",
        readTime: "8 min",
        views: "7.1k",
        rating: 4.6
      },
      {
        title: "Community Resources and Links",
        description: "Useful community tools and websites",
        readTime: "5 min",
        views: "5.8k",
        rating: 4.4
      }
    ]
  }
];

export default function KnowledgeBase() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900">
      {/* Header */}
      <div className="relative pt-20 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center">
            <div className="flex items-center justify-center mb-6">
              <BookOpen className="w-12 h-12 text-cyan-400 mr-4" />
              <h1 className="text-4xl md:text-5xl font-bold text-white">
                Knowledge Base <span className="text-amber-400 text-2xl md:text-3xl">(under construction)</span>
              </h1>
            </div>
            <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
              Comprehensive guides and tutorials for mastering Stellar Blade modding
            </p>
            
            {/* Breadcrumb */}
            <nav className="flex items-center justify-center space-x-2 text-sm">
              <Link to="/wiki" className="text-cyan-400 hover:text-cyan-300">
                Wiki
              </Link>
              <span className="text-gray-500">/</span>
              <span className="text-gray-300">Knowledge Base</span>
            </nav>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        {/* Categories Grid */}
        <div className="space-y-12">
          {categories.map((category) => (
            <div key={category.id} className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-8">
              {/* Category Header */}
              <div className="flex items-center mb-8">
                <div className={`p-3 bg-gradient-to-r ${category.color} rounded-lg mr-4`}>
                  <category.icon className="w-8 h-8 text-white" />
                </div>
                <div className="flex-1">
                  <h2 className="text-3xl font-bold text-white mb-2">{category.title}</h2>
                  <p className="text-gray-400">{category.description}</p>
                </div>
                <Link 
                  to={`/wiki/knowledge-base/${category.id}`}
                  className="flex items-center text-cyan-400 hover:text-cyan-300 font-medium"
                >
                  View All
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Link>
              </div>

              {/* Articles Grid */}
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {category.articles.map((article, index) => (
                  <Link
                    key={index}
                    to={
                      article.title === "Basic Mod Installation Guide" 
                        ? `/wiki/knowledge-base/installation` 
                        : article.title === "Suspicious Mod Files and Security Checks"
                        ? `/wiki/knowledge-base/file-security`
                        : `/wiki/knowledge-base/${category.id}/${article.title.toLowerCase().replace(/\s+/g, '-')}`
                    }
                    className="group"
                  >
                    <div className={`bg-white/5 backdrop-blur-sm border border-white/10 rounded-lg p-6 hover:bg-white/10 transition-all duration-300 hover:${category.borderColor} hover:shadow-lg hover:${category.shadowColor}`}>
                      {/* Article Meta */}
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center space-x-4 text-sm text-gray-400">
                          <div className="flex items-center">
                            <Clock className="w-4 h-4 mr-1" />
                            {article.readTime}
                          </div>
                          <div className="flex items-center">
                            <Eye className="w-4 h-4 mr-1" />
                            {article.views}
                          </div>
                        </div>
                        <div className="flex items-center text-yellow-400">
                          <Star className="w-4 h-4 mr-1 fill-current" />
                          <span className="text-sm font-medium">{article.rating}</span>
                        </div>
                      </div>

                      {/* Article Content */}
                      <h3 className="text-lg font-semibold text-white mb-3 group-hover:text-cyan-300 transition-colors">
                        {article.title}
                      </h3>
                      <p className="text-gray-400 text-sm mb-4 line-clamp-2">
                        {article.description}
                      </p>

                      {/* Read More */}
                      <div className="flex items-center text-cyan-400 group-hover:text-cyan-300 text-sm font-medium">
                        <span>Read Article</span>
                        <ArrowRight className="w-4 h-4 ml-1 transform group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Bottom Navigation */}
        <div className="mt-16 text-center">
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-8">
            <h3 className="text-2xl font-bold text-white mb-4">Need Quick Help?</h3>
            <p className="text-gray-300 mb-6">
              Can't find what you're looking for? Check our FAQ section for quick answers to common questions.
            </p>
            <Link
              to="/wiki/faq"
              className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-purple-400 to-pink-500 text-white font-semibold rounded-lg hover:from-purple-500 hover:to-pink-600 transition-all duration-300"
            >
              Browse FAQ
              <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
} 